import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { LuChevronDown, LuChevronLeft, LuCheck, LuLock } from "react-icons/lu";
import { colors, typography, effects, components } from "../../../design/tokens";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import {
  AVAILABLE_MODELS,
  ModelInfo,
  useSelectedModel,
  useSelectedModelInfo,
  useSetSelectedModel,
  ModelId,
} from "../../../core_stores/useModelStore";
import ClaudeAISymbol from "../../../assets/claude-ai-symbol.svg?react";
import { useUserSubscription } from "../../../core_stores/useSubscriptionStore";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";

interface ModelSelectorBtnProps {
  className?: string;
  isDisabled?: boolean;
}

export const ModelSelectorBtn: React.FC<ModelSelectorBtnProps> = ({
  className = "",
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredModelId, setHoveredModelId] = useState<string | null>(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const isCompactView = useIsCompactView();
  const selectedModel = useSelectedModel();
  const selectedModelInfo = useSelectedModelInfo();
  const setSelectedModel = useSetSelectedModel();
  const subscription = useUserSubscription();
  const isFreePlan = !subscription || subscription.free;
  const { open: openSubscriptionsModal } = useSubscriptionsModal();

  // Update button position when dropdown opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.top,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside BOTH the button container AND the portal dropdown
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsidePortal = !portalRef.current || !portalRef.current.contains(target);

      if (isOutsideButton && isOutsidePortal) {
        setIsOpen(false);
        setHoveredModelId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleModelSelect = (modelId: ModelId) => {
    setSelectedModel(modelId);
    setIsOpen(false);
    setHoveredModelId(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setHoveredModelId(null);
  };

  const hoveredModel = hoveredModelId
    ? AVAILABLE_MODELS.find((m) => m.id === hoveredModelId) ?? null
    : null;

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      {/* Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`${className} flex items-center justify-center gap-1.5 ${effects.transition.default}`}
        type="button"
        disabled={isDisabled}
        aria-label="Select AI Model"
      >
        {selectedModel === "auto" && (
          <ClaudeAISymbol
            className="w-4 h-4"
            style={{ fill: colors.hex.textSecondary }}
          />
        )}
        <span
          className={`${typography.size.sm} ${typography.weight.medium} ${colors.text.secondary}`}
        >
          {selectedModelInfo.name}
        </span>
        <LuChevronDown
          size={14}
          className={`${colors.text.secondary} transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen &&
        (isCompactView ? (
          <MobileModelModal
            models={AVAILABLE_MODELS}
            selectedModel={selectedModel}
            onSelect={handleModelSelect}
            onClose={handleClose}
            isFreePlan={isFreePlan}
            onUpgradeClick={openSubscriptionsModal}
            portalRef={portalRef}
          />
        ) : (
          <DesktopModelDropdown
            models={AVAILABLE_MODELS}
            selectedModel={selectedModel}
            hoveredModel={hoveredModel}
            onSelect={handleModelSelect}
            onHover={setHoveredModelId}
            onClose={handleClose}
            position={buttonPosition}
            portalRef={portalRef}
            isFreePlan={isFreePlan}
            onUpgradeClick={openSubscriptionsModal}
          />
        ))}
    </div>
  );
};

// ============================================================================
// DESKTOP DROPDOWN
// ============================================================================

interface DesktopModelDropdownProps {
  models: ModelInfo[];
  selectedModel: ModelId;
  hoveredModel: ModelInfo | null;
  onSelect: (modelId: ModelId) => void;
  onHover: (modelId: string | null) => void;
  onClose: () => void;
  position: { top: number; left: number };
  portalRef: React.RefObject<HTMLDivElement>;
  isFreePlan: boolean;
  onUpgradeClick: () => void;
}

function DesktopModelDropdown({
  models,
  selectedModel,
  hoveredModel,
  onSelect,
  onHover,
  onClose,
  position,
  portalRef,
  isFreePlan,
  onUpgradeClick,
}: DesktopModelDropdownProps) {
  // Show hovered model details, or selected model if nothing hovered
  const displayedModel =
    hoveredModel || models.find((m) => m.id === selectedModel) || models[0];

  return createPortal(
    <div
      ref={portalRef}
      style={{
        position: "fixed",
        top: position.top - 8,
        left: position.left,
        transform: "translateY(-100%)",
      }}
      className={`z-50 flex bg-black border border-gray-800 rounded-lg
        shadow-[0_2px_12px_rgba(0,0,0,0.4)]`}
    >
      {/* Left Column - Model List */}
      <div className="w-48 border-r border-gray-800 py-1">
        {models.map((model) => {
          const isSelected = selectedModel === model.id;
          const isHovered = hoveredModel?.id === model.id;
          const isLocked = isFreePlan && model.id !== "auto";
          return (
            <button
              key={model.id}
              onClick={() => {
                if (isLocked) {
                  onClose();
                  onUpgradeClick();
                } else {
                  onSelect(model.id);
                }
              }}
              onMouseEnter={() => onHover(model.id)}
              onMouseLeave={() => onHover(null)}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-2
                ${typography.size.sm} ${typography.weight.medium}
                ${effects.transition.default}
                ${isHovered || isSelected ? "bg-white/10" : "hover:bg-white/5"}
                ${isLocked ? "text-dark-text-muted" : colors.text.primary}`}
            >
              <span className="flex-1">{model.name}</span>
              {isSelected && (
                <span className={`${colors.text.secondary} text-xs`}>
                  ACTIVE
                </span>
              )}
              {isLocked && !isSelected && (
                <div className="relative group/lock">
                  <LuLock size={14} className="text-dark-text-muted" />
                  {/* Tooltip on hover */}
                  <div className="absolute right-0 bottom-full mb-1
                    opacity-0 group-hover/lock:opacity-100 pointer-events-none
                    transition-opacity duration-200
                    bg-black border border-gray-700 rounded px-2 py-1
                    text-xs text-white whitespace-nowrap z-50">
                    Upgrade to unlock
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Right Column - Model Details */}
      <div className="w-[340px] p-4">
        {/* Model name - fixed 1 line height */}
        <div className="flex items-center gap-2 mb-2 h-6">
          <ClaudeAISymbol
            className="w-5 h-5 flex-shrink-0"
            style={{ fill: "#d97757" }}
          />
          <h4
            className={`${typography.size.base} ${typography.weight.semibold} ${colors.text.primary} line-clamp-1`}
          >
            {displayedModel.fullName}
          </h4>
        </div>

        {/* Description - fixed 2 lines height */}
        <p
          className={`${typography.size.sm} ${colors.text.secondary} mb-3 leading-relaxed line-clamp-2 h-[44px]`}
        >
          {displayedModel.description}
        </p>

        {/* Token usage description - fixed 2 lines height */}
        <p
          className={`${typography.size.sm} mb-4 leading-relaxed line-clamp-2 h-[44px]`}
          style={{ color: colors.hex.textSecondary }}
        >
          {displayedModel.tokenUsageDescription}
        </p>

        {/* Progress Bars */}
        <div className="space-y-3">
          <ProgressBar label="Speed" value={displayedModel.speed} />
          <ProgressBar label="Intelligence" value={displayedModel.intelligence} />
          <ProgressBar label="UI design" value={displayedModel.uiDesign} />
          <ProgressBar label="Token usage" value={displayedModel.tokenUsage} />
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// MOBILE MODAL
// ============================================================================

interface MobileModelModalProps {
  models: ModelInfo[];
  selectedModel: ModelId;
  onSelect: (modelId: ModelId) => void;
  onClose: () => void;
  isFreePlan: boolean;
  onUpgradeClick: () => void;
  portalRef: React.RefObject<HTMLDivElement>;
}

function MobileModelModal({
  models,
  selectedModel,
  onSelect,
  onClose,
  isFreePlan,
  onUpgradeClick,
  portalRef,
}: MobileModelModalProps) {
  return createPortal(
    <div
      ref={portalRef}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: colors.hex.surfaceSecondary }}
    >
      {/* Header */}
      <div className="flex items-center px-4 py-4 border-b border-dark-border">
        <button
          onClick={onClose}
          className={`${components.modal.closeButton} mr-4`}
          aria-label="Close"
        >
          <LuChevronLeft size={24} />
        </button>
        <h2
          className={`${typography.size["xl"]} ${typography.weight.bold} ${colors.text.primary}`}
        >
          Select Model
        </h2>
      </div>

      {/* Model List */}
      <div className="flex-1 overflow-y-auto p-4">
        {models.map((model) => {
          const isSelected = selectedModel === model.id;
          const isLocked = isFreePlan && model.id !== "auto";
          return (
            <button
              key={model.id}
              onClick={() => {
                if (isLocked) {
                  onClose();
                  onUpgradeClick();
                } else {
                  onSelect(model.id);
                  onClose();
                }
              }}
              className={`w-full text-left p-4 mb-3 rounded-xl border
                ${effects.transition.default}
                ${
                  isSelected
                    ? "border-primary-blue bg-primary-blue/10"
                    : isLocked
                    ? "border-dark-border bg-dark-surface-primary/50"
                    : "border-dark-border bg-dark-surface-primary"
                }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <ClaudeAISymbol
                  className="w-5 h-5 flex-shrink-0"
                  style={{ fill: isLocked ? "#d9775766" : "#d97757" }}
                />
                <span
                  className={`${typography.size.base} ${typography.weight.semibold} ${isLocked ? "text-dark-text-muted" : colors.text.primary} flex-1`}
                >
                  {model.fullName}
                </span>
                {isSelected && (
                  <LuCheck size={20} className="text-primary-blue" />
                )}
                {isLocked && !isSelected && (
                  <LuLock size={18} className="text-dark-text-muted" />
                )}
              </div>
              <p
                className={`${typography.size.sm} ${isLocked ? "text-dark-text-muted" : colors.text.secondary} mb-2`}
              >
                {model.description}
              </p>
              <p
                className={`${typography.size.xs}`}
                style={{ color: colors.hex.textSecondary }}
              >
                {model.tokenUsageDescription}
              </p>
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// PROGRESS BAR
// ============================================================================

interface ProgressBarProps {
  label: string;
  value: number;
  maxValue?: number;
}

function ProgressBar({ label, value, maxValue = 10 }: ProgressBarProps) {
  const percentage = (value / maxValue) * 100;

  return (
    <div className="flex items-center gap-3">
      <span
        className={`${typography.size.xs} ${colors.text.secondary} w-20 flex-shrink-0`}
      >
        {label}
      </span>
      <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary-blue rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
